import { AxiosInstance } from 'axios';
import { getParams } from '../../../core/api/axios';
import { TypeParams } from '../../../core/utils/type-params';
import { ItemInfo, ItemPatch } from '../../../shared/types/itemInfo';
import { FeatureCollection, PolyCoords } from '../../../shared/types/MapShapes';
import {
    FilterOption,
    ItemPort,
    ItemSaveResponse,
    PolygonAreaResponse,
    PreloadedFilter,
    PreloadedFilterOptionResponse,
    PreloadedFilterOptions,
    PreloadedDepartmentLimitResponse,
    PreloadedFilterOptionsResponse,
    PreloadedItemsFilters,
    PreloadedItemsResponse,
    RanchCentroidResponse,
    RanchItemResponse
} from '../../../core/api/ports';
import { ItemUrls } from './ranch-item-urls';
import { getPolygonCentroidFromUrl } from '../../../shared/helpers/get-centroid-from-url';

/** `:id` de la URL → id numérico del ranch, o `null` si no es un entero positivo. */
export const toNumericItemId = (itemId: string | number | null | undefined): number | null => {
    const raw = itemId === null || itemId === undefined ? '' : String(itemId).trim();
    return /^\d+$/.test(raw) && Number(raw) > 0 ? Number(raw) : null;
};

/** `centroid` de `GET /ranch/` → `{ lat, lng }`, o `null` si falta o alguna coordenada no es un número finito. */
export const centroidFromRanch = (
    centroid: RanchCentroidResponse | null | undefined
): { lat: number; lng: number } | null => {
    const lat = centroid ? centroid.latitude : null;
    const lng = centroid ? centroid.longitude : null;
    return typeof lat === 'number' && typeof lng === 'number' && Number.isFinite(lat) && Number.isFinite(lng)
        ? { lat, lng }
        : null;
};

/** Centroide del item: `centroid` de `GET /ranch/` y, si no es válido, `centroid_lat`/`centroid_long` de la `redirect_url`. */
export const toItemCentroid = (data: Pick<RanchItemResponse, 'centroid' | 'redirect_url'>): { lat: number; lng: number } | null =>
    centroidFromRanch(data.centroid) || (data.redirect_url ? getPolygonCentroidFromUrl(data.redirect_url) : null);

/** `GET /ranch/` → `ItemInfo`. Los campos opcionales que falten quedan `null` (la página los tolera). */
export const toItemInfo = (data: RanchItemResponse): ItemInfo => ({
    ...data,
    polygon: data.polygon || null,
    department_polygon: data.department_polygon || null,
    centroidPoint: toItemCentroid(data)
});

/** Clave de `GET /ranch/list/options/` con las opciones de cada filtro de lotes pre-cargados. */
export const PRELOADED_FILTER_OPTION_KEYS: Record<PreloadedFilter, keyof PreloadedFilterOptionsResponse> = {
    [PreloadedFilter.Campaign]: 'years',
    [PreloadedFilter.Department]: 'departments',
    [PreloadedFilter.CropType]: 'crop_types'
};

/**
 * Una entrada de un endpoint de opciones (`{ id, description }`) → `FilterOption`, o `null` si le falta `id` o
 * `description`. El id se pasa a texto (backend puede mandar número o texto).
 */
export const toFilterOption = (raw: PreloadedFilterOptionResponse | null | undefined): FilterOption | null => {
    if (!raw || typeof raw !== 'object') return null;
    const id = raw.id === null || raw.id === undefined ? '' : String(raw.id).trim();
    const label = typeof raw.description === 'string' ? raw.description.trim() : '';
    return id && label ? { id, label } : null;
};

/** Lista cruda de opciones → `FilterOption[]`, sin entradas inválidas y en el orden de backend. */
export const toFilterOptions = (data: unknown): FilterOption[] =>
    Array.isArray(data)
        ? data
              .map(raw => toFilterOption(raw as PreloadedFilterOptionResponse))
              .filter((option): option is FilterOption => option !== null)
        : [];

/**
 * `GET /ranch/list/options/` → opciones de cada filtro (`PRELOADED_FILTER_OPTION_KEYS`). Una clave ausente o que no
 * sea lista → `[]`; una respuesta que no es objeto → todos vacíos.
 */
export const toPreloadedFilterOptions = (data: unknown): PreloadedFilterOptions => {
    const raw = (data && typeof data === 'object' && !Array.isArray(data) ? data : {}) as PreloadedFilterOptionsResponse;
    return {
        [PreloadedFilter.Campaign]: toFilterOptions(raw[PRELOADED_FILTER_OPTION_KEYS[PreloadedFilter.Campaign]]),
        [PreloadedFilter.Department]: toFilterOptions(raw[PRELOADED_FILTER_OPTION_KEYS[PreloadedFilter.Department]]),
        [PreloadedFilter.CropType]: toFilterOptions(raw[PRELOADED_FILTER_OPTION_KEYS[PreloadedFilter.CropType]])
    };
};

/**
 * Ids de un filtro → CSV de enteros (`"2022,2023"`), o `null` si no queda ninguno. Backend responde 400 si algún
 * valor no es entero, así que se descarta lo que no lo sea.
 */
export const toIntegerCsv = (ids: ReadonlyArray<string | number> | null | undefined): string | null => {
    const valid = (ids || []).map(id => String(id).trim()).filter(id => /^-?\d+$/.test(id));
    return valid.length > 0 ? valid.join(',') : null;
};

/**
 * Parámetros de los filtros de `GET /ranch/list/`: cada uno como CSV, solo si tiene ids válidos (vacío = todos, el
 * parámetro se omite).
 */
export const preloadedItemsParams = (filters: PreloadedItemsFilters | null | undefined): Record<string, string> => {
    const params: Record<string, string> = {};
    const entries: Array<[TypeParams, string[] | undefined]> = [
        [TypeParams.YEARS, filters ? filters.campaigns : undefined],
        [TypeParams.DEPARTMENTS, filters ? filters.departments : undefined],
        [TypeParams.CROP_TYPES, filters ? filters.cropTypes : undefined]
    ];
    entries.forEach(([key, ids]) => {
        const csv = toIntegerCsv(ids);
        if (csv !== null) params[key] = csv;
    });
    return params;
};

/**
 * `{ customer }` solo si `customerId` es un número (`0` es un id válido, por eso no se usa `getParams`, que descarta
 * valores falsy); si no, `{}` y el parámetro se omite.
 */
export const customerParams = (customerId?: number | null): Record<string, number> =>
    typeof customerId === 'number' && Number.isFinite(customerId) ? { [TypeParams.CUSTOMER]: customerId } : {};

/**
 * `{ ranch_id }` solo si `ranchId` es un número (el `id` de `GET /ranch/`, no el `:id` de la URL); si no, `{}` y el
 * parámetro se omite.
 */
export const ranchIdParams = (ranchId?: number | null): Record<string, number> =>
    typeof ranchId === 'number' && Number.isFinite(ranchId) ? { [TypeParams.RANCH_ID]: ranchId } : {};

/** `departments` de `/ranch/list/`: solo se acepta una FeatureCollection; si no viene o es otra cosa → `null`. */
const isFeatureCollection = (value: any): value is FeatureCollection =>
    !!value && value.type === 'FeatureCollection' && Array.isArray(value.features);

/**
 * `departments` de `/ranch/list/` → una sola FeatureCollection para el mapa.
 * - Formato de backend: `PreloadedDepartmentLimitResponse[]` (`{ id, name, polygon }`); los de `polygon: null` se omiten.
 * - Backend repite `id: "0"` en los features de cada departamento, y en una capa de Google Maps un id repetido
 *   reemplaza al feature anterior (se vería uno solo): cada feature recibe un id único y el `id`/`name` del
 *   departamento en `properties`.
 * - También acepta una FeatureCollection directa. Sin features dibujables → `null`.
 */
export const toDepartmentsLayer = (raw: any): FeatureCollection | null => {
    let sources: Array<{ departmentId: number | string; name?: string; polygon: FeatureCollection }> = [];
    if (Array.isArray(raw)) {
        sources = (raw as PreloadedDepartmentLimitResponse[])
            .filter(department => department && isFeatureCollection(department.polygon))
            .map(department => ({ departmentId: department.id, name: department.name, polygon: department.polygon! }));
    } else if (isFeatureCollection(raw)) {
        sources = [{ departmentId: 'layer', polygon: raw }];
    }
    const features = sources.reduce((all: any[], source) => {
        source.polygon.features.forEach((feature: any, index: number) => {
            const properties = { ...(feature.properties || {}) };
            if (source.departmentId !== 'layer') {
                properties.id = source.departmentId;
                if (source.name) properties.name = source.name;
            }
            all.push({ ...feature, id: `department-${source.departmentId}-${index}`, properties });
        });
        return all;
    }, []);
    return features.length > 0 ? ({ type: 'FeatureCollection', features } as FeatureCollection) : null;
};

/** Respuesta de `/ranch/list/` normalizada (hoy solo `departments` puede faltar). */
export const toPreloadedItemsResponse = (raw: any): PreloadedItemsResponse => ({
    ...(raw || {}),
    list: raw && Array.isArray(raw.list) ? raw.list : [],
    polygons: raw ? raw.polygons : null,
    departments: toDepartmentsLayer(raw && raw.departments)
});

/**
 * Adaptador de `ItemPort` sobre `/ranch/...`. Los backends de Zurich y tasaciones exponen estos endpoints con el mismo
 * contrato, así que ambos tenants reutilizan esta clase (antes `features/core/api/ItemServices.ts`, mismos requests).
 * `/farming/list/` (`getFarmingList`) no se portó: no tenía llamadores en ningún repo.
 */
export class RanchItemApi implements ItemPort {
    constructor(private readonly http: AxiosInstance) {}

    /**
     * Detalle de item. El `:id` de la URL solo se manda como `?id=` si es numérico (ids de ranch de Zurich); si es
     * un identificador de texto (tasaciones, p. ej. `old-858397_...`) se omite y el backend resuelve el item por el
     * token. Es lo mismo que pasaba antes con `Number(id)` = `NaN`, que `getParams` descartaba.
     */
    getItemDetail(itemId: string): Promise<ItemInfo> {
        const numericId = toNumericItemId(itemId);
        return this.http
            .get<RanchItemResponse>(`${ItemUrls.RANCH}`, {
                params: getParams([{ key: TypeParams.ID, value: numericId }])
            })
            .then(response => toItemInfo(response.data));
    }

    //Enviar item
    addPolygonToItem(config: ItemPatch): Promise<ItemSaveResponse> {
        return this.http.patch(`${ItemUrls.RANCH}`, config).then(response => response.data);
    }

    /** Opciones de los tres filtros de lotes pre-cargados (un solo `GET /ranch/list/options/`), normalizadas. */
    getPreloadedFilterOptions(customerId?: number | null, ranchId?: number | null): Promise<PreloadedFilterOptions> {
        return this.http
            .get<PreloadedFilterOptionsResponse>(`${ItemUrls.RANCH_LIST_OPTIONS}`, {
                params: { ...customerParams(customerId), ...ranchIdParams(ranchId) }
            })
            .then(response => toPreloadedFilterOptions(response.data));
    }

    /**
     * Lotes pre-cargados (`GET /ranch/list/?years=2022,2023&departments=3&crop_types=1`). El join se hace acá porque
     * `Http` no tiene un `paramsSerializer` global. Un filtro vacío = todos (backend no filtra si falta el parámetro).
     * `customer` va si hay aseguradora y `ranch_id` si se conoce el item.
     */
    getPreloadedItems(
        filters: PreloadedItemsFilters,
        customerId?: number | null,
        ranchId?: number | null
    ): Promise<PreloadedItemsResponse> {
        return this.http
            .get<PreloadedItemsResponse>(`${ItemUrls.RANCH_LIST}`, {
                params: {
                    ...preloadedItemsParams(filters),
                    ...customerParams(customerId),
                    ...ranchIdParams(ranchId)
                }
            })
            .then(response => toPreloadedItemsResponse(response.data));
    }

    //KML/KMZ to GeoJson
    getGeojsonFile(formData: FormData): Promise<FeatureCollection> {
        return this.http.post(`${ItemUrls.PROCESS_FILE}`, formData).then(response => response.data);
    }

    //Get area for polygon
    getPolygonArea(config: { polygon: PolyCoords }): Promise<PolygonAreaResponse> {
        return this.http.post(`${ItemUrls.RANCH_AREA}`, config).then(response => response.data);
    }
}
